import React, { ChangeEvent, FocusEvent, ForwardedRef, useCallback, useContext } from 'react';
import { ThemeContext } from 'styled-components';

import excludePropsByComponent from '../../../utils/components/_excludePropsByComponent';
import Control, { ControlProps, ControlRenderProps } from '../Control';
import RadioList, { RadioListProps } from '../RadioList';

type IgnoreProps = 'defaultValue' | 'name' | 'value' | 'isValid' | 'isTouched';

export type RadioListControlValidationProps = {
  /** Adds the `required` validator to the control. The control will be invalid if the value is empty. */
  required?: boolean;
  /** Adds the `recommended` validator to the control. The control will display a warning if the value is empty. */
  recommended?: boolean;
};

export interface RadioListControlProps<Value, StateValue> extends
    Omit<RadioListProps<Value>, IgnoreProps>,
    Omit<ControlProps<Value, StateValue>, 'children'>,
    RadioListControlValidationProps {
  ref?: ForwardedRef<HTMLInputElement>;
}

interface RadioListControlInternalProps<Value> extends Omit<ControlRenderProps<Value, HTMLInputElement>, 'onChange' | 'onBlur' | 'onFocus' | 'onKeyDown' | 'id'> {
}

export interface RadioListControlComposedProps<Value, StateValue> extends RadioListControlProps<Value, StateValue>, RadioListControlInternalProps<Value> {

}

export const RadioListControlDefaultProps = {};

const RadioListControl = <Value, StateValue>({
  id,
  name,
  required,
  defaultValue,
  options,
  noBox,
  readOnly,
  textOnly,
  isValid,
  isTouched,
  errorMessagesId,
  value,
  onChange,
  onBlur,
  onValueChange,
  ...props
}: RadioListControlComposedProps<Value, StateValue>) => {
  const theme = useContext(ThemeContext);
  const resolvedNoBox = (typeof noBox !== 'undefined') ? noBox : theme?.RadioList?.noBox;

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const index = Number(event.target.getAttribute('data-index'));
    const item = options[index];
    onValueChange(item.value, { updateType: 'change' });

    if (onChange) {
      onChange(event);
    }
  }, [onChange, onValueChange, options]);

  const handleBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    const index = Number(event.target.getAttribute('data-index'));
    const item = options[index];
    onValueChange(item.value);

    if (onBlur) {
      onBlur(event);
    }
  }, [onBlur, onValueChange, options]);

  return (
    <RadioList<Value>
      id={id}
      data-control
      data-control-nesting="5"
      aria-invalid={Boolean(!isValid && isTouched)}
      aria-errormessage={errorMessagesId}
      readOnly={readOnly}
      textOnly={textOnly}
      {...excludePropsByComponent(props, Control)}
      options={options}
      noBox={resolvedNoBox}
      name={name}
      value={value}
      isValid={isValid}
      isTouched={isTouched}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

RadioListControl.defaultProps = RadioListControlDefaultProps;

export default RadioListControl;
